import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '../i18n';
import { usePwaInstall } from '../hooks/usePwaInstall';
import './InstallAppButton.css';

// ── INSTALL APP ──────────────────────────────────────────────────────────────
// A row in the user menu that installs Tunify to the desktop or home screen.
//
// It RENDERS NOTHING unless there is something to offer: no row when the app is
// already installed, and none in a browser that cannot install at all. An
// "Install app" item that does nothing is worse than no item.
//
// Two behaviours behind one row:
//   • Chrome / Edge / Android — opens the browser's own install dialog.
//   • iOS Safari              — has no install API, so it opens a short sheet
//                               showing where the Share button is. Telling
//                               someone to do it by hand is not as good as
//                               doing it for them, but it is the only thing iOS
//                               allows, and saying nothing leaves the feature
//                               invisible to every iPhone user.
//
// `style` is passed in by UserMenu so this row matches the ones around it —
// that menu styles its items inline, and a component that ignored it would be
// the one row that looked wrong.
export default function InstallAppButton({ style, onDone }) {
  const { t } = useT();
  const { canInstall, needsIOSHelp, install } = usePwaInstall();
  const [showIOS, setShowIOS] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!canInstall && !needsIOSHelp) return null;

  async function handleClick() {
    if (canInstall) {
      setBusy(true);
      const outcome = await install();
      setBusy(false);
      // 'dismissed' needs no message: they just closed a dialog on purpose, and
      // telling them what they already did is noise. The row disappears by
      // itself once the app is installed.
      if (outcome === 'accepted') onDone?.();
      return;
    }
    setShowIOS(true);
  }

  return (
    <>
      <button type="button" onClick={handleClick} disabled={busy} style={style}>
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
          strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        {t('pwa_install')}
      </button>

      {/* PORTALLED TO <body>, and it has to be.

          This row lives inside the user dropdown, and that dropdown animates
          with a transform. A transform creates a containing block, so a
          position:fixed child is positioned against the DROPDOWN rather than
          the viewport — the sheet was being laid out inside a ~260px menu, with
          its title clipped off the top on a real iPhone. A portal lifts it out
          of that subtree, which is the only fix that does not mean removing the
          menu's open/close animation. */}
      {showIOS && createPortal(
        <div
          className="iab-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setShowIOS(false); }}
          role="dialog"
          aria-modal="true"
          aria-label={t('pwa_install')}
        >
          <div className="iab-sheet">
            <div className="iab-head">
              <span className="iab-title">{t('pwa_ios_title')}</span>
              <button
                type="button"
                className="iab-close"
                onClick={() => setShowIOS(false)}
                aria-label={t('cancel')}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none"
                  stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <ol className="iab-steps">
              <li>
                {/* The real Share glyph, drawn rather than described — "the
                    share button" is not obvious to everyone, and the icon is
                    what they are actually looking for on the toolbar. */}
                <span className="iab-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
                    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 15V3" /><polyline points="8 7 12 3 16 7" />
                    <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
                  </svg>
                </span>
                <span>{t('pwa_ios_step1')}</span>
              </li>
              <li>
                <span className="iab-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
                    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="4" />
                    <line x1="12" y1="8" x2="12" y2="16" /><line x1="8" y1="12" x2="16" y2="12" />
                  </svg>
                </span>
                <span>{t('pwa_ios_step2')}</span>
              </li>
            </ol>

            <p className="iab-note">{t('pwa_ios_note')}</p>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
